import { useCallback, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { BibleComponentProps, BibleVerse, BibleFilter } from "../models";
import { useBibleSearch } from "../api/search";
import { Helmet } from "react-helmet-async";
import BarLoader from "react-spinners/BarLoader";
import { setBible } from "../store/slices/bible";
import { toggleBiblePopOut } from "../store/slices/settings";
import { useAppSelector, useAppDispatch } from "../store";
import { FaArrowAltCircleUp } from "react-icons/fa";
import { HiClipboardCopy } from "react-icons/hi";

export function BibleSearch() {
  const settings = useAppSelector((state: any) => state.settings);
  let [searchParams, setSearchParams] = useSearchParams();
  const filterText = searchParams.get("bookFilter") ?? "";
  let [searchText, setSearchText] = useState<string>(
    searchParams.get("text") ?? ""
  );
  let [test, setTest] = useState<string>(searchParams.get("text") ?? "");
  const setSearchTextUrl = useCallback(
    (newSearchText: string, filterText: string) => {
      setSearchParams(
        {
          ...searchParams,
          text: newSearchText,
          bookFilter: filterText,
        },
        {
          replace: true,
        }
      );
    },
    [searchParams, setSearchParams]
  );
  const handleSearch = () => {
    setSearchText(test);
    setSearchTextUrl(test, "");
  };

  const { data, isLoading, isError } = useBibleSearch(searchText, filterText);
  const { verses, error, totalVerses, filters } = data || {};

  if (error && error.message) console.log("error message:", error?.message);
  if (error && error.innerException)
    console.log("error innerException:", error?.innerException);

  const myRef = useRef<null | HTMLDivElement>(null);
  const executeScroll = () =>
    myRef?.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div ref={myRef} className="bible-search-container">
      <div className="main-search-container">
        <Helmet>
          <title>GodResource Bible Search: {searchText}</title>
        </Helmet>
        <div className="main-search-header">
          <h1>Bible Search</h1>
          <div className="main-search-input-container">
            <input
              autoFocus
              className="main-search"
              onChange={(e) => setTest(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSearch();
                }
              }}
              value={test}
              placeholder="search"
            />
            <button className="main-search-button" onClick={handleSearch}>
              Search
            </button>
          </div>
          {isError ? <p>Something isn't working...</p> : null}
          {error && error.message ? (
            <p className="error-message">
              Oops, something went wrong. Please try again later.
            </p>
          ) : null}
        </div>
        {searchText.length < 3 ? (
          <p className="search-instructions">
            Please enter at least 3 characters to search the Bible.
          </p>
        ) : null}
        {isLoading ? (
          <div className="loader-container">
            <BarLoader className="loader" />
          </div>
        ) : null}
        {verses ? (
          <SearchResults
            totalVerses={totalVerses}
            searchText={searchText}
            verses={verses}
            isLoading={isLoading}
            filters={filters}
            filterText={filterText}
            setSearchText={setSearchTextUrl}
          />
        ) : null}
      </div>
      {settings.contentScrollTop > 200 ? (
        <span className="scroll-arrow" onClick={executeScroll}>
          <FaArrowAltCircleUp size={40} />
        </span>
      ) : null}
    </div>
  );
}

function SearchResults({
  totalVerses,
  searchText,
  verses,
  isLoading,
  filters,
  setSearchText,
  filterText,
}: {
  totalVerses: number | undefined;
  searchText: string;
  verses: BibleVerse[];
  isLoading: boolean;
  filters: BibleFilter[] | undefined;
  setSearchText: any;
  filterText: any;
}) {
  console.log(filters);
  return (
    <div className="main-search-results">
      <div className="main-search-results-container">
        <div className="main-search-results-inner">
          <ul>
            {totalVerses && totalVerses > 0 ? (
              <div>
                <span>
                  {totalVerses.toLocaleString()} verses matched your search.
                  Showing {verses.length.toLocaleString()} verses.
                </span>
                {verses.map((verse: any, i: number) => (
                  <SearchResultsVerse key={i} verse={verse} />
                ))}
              </div>
            ) : searchText ? (
              <li>no results</li>
            ) : (
              <li>please enter search criterion</li>
            )}
          </ul>
        </div>
      </div>
      {totalVerses && totalVerses > 0 ? (
        <div className="main-filters-container">
          <div className="main-filters-inner">
            <p style={{ textDecoration: "underline", fontWeight: "bold" }}>
              Filters:
            </p>
            {filters?.map((filter: any, i: number) => (
              <SearchResultsFilter
                key={i}
                filter={filter}
                searchText={searchText}
                setSearchText={setSearchText}
                filterText={filterText}
              />
            ))}
          </div>
        </div>
      ) : (
        ""
      )}
    </div>
  );
}

function SearchResultsVerse({ verse }: { verse: any }) {
  const dispatch = useAppDispatch();

  const handleVerseHeaderClick = (verse: any) => {
    const b: BibleComponentProps = {
      version: "kjv",
      chapter: verse.bookName,
      bookId: verse.bookID,
      chapterId: verse.chapterID,
      verseId: verse.verseID,
    };
    dispatch(setBible(b));
    dispatch(toggleBiblePopOut(true));
  };
  const regex = /(<([^>]+)>)/gi;
  const header = `${verse.bookName} ${verse.chapterID}:${verse.verseID}`;
  return (
    <li style={{ float: "left", width: "100%", padding: "10px 0" }}>
      <span
        className="bible-search-header"
        onClick={() => handleVerseHeaderClick(verse)}
      >
        <b>{header}</b>
      </span>
      {getHighlightedText(verse.verseText)}
      <div
        className="bible-verse-copy"
        onClick={() => {
          navigator.clipboard.writeText(
            `${header} ${verse.verseText.replace(regex, "")}`
          );
        }}
      >
       copy <HiClipboardCopy /> 
      </div>
    </li>
  );
}

function SearchResultsFilter({
  filter,
  searchText,
  setSearchText,
  filterText,
}: {
  filter: any;
  searchText: any;
  setSearchText: any;
  filterText: any;
}) {
  if (filter.matches <= 0) return null;

  const handleFilterClick = (id: any) => {
    setSearchText(searchText, id.toString());
  };

  //-- Create the first one for All && New Testament && Old Testament
  return (
    <li style={{ float: "left", width: "100%", padding: "3px 0 0" }}>
      <span
        className={
          "bible-filter-header" +
          (filter.bookID.toString() === filterText ? " active-filter" : "")
        }
        onClick={() => handleFilterClick(filter.bookID)}
      >
        {filter.isHeader ? (
          <b>
            {filter.bookName} ({filter.matches})
          </b>
        ) : (
          <span>
            {filter.bookName} ({filter.matches})
          </span>
        )}
      </span>
    </li>
  );
}

const emRegex = /(<em>[a-zA-Z]+<\/em>)/gi;

function getHighlightedText(text: string) {
  const parts = text.split(emRegex);
  return (
    <span>
      {parts.map((part, i) => {
        if (!part.includes("<em>")) {
          return <span key={i}>{part}</span>;
        } else {
          return (
            <span key={i} className="highlighted">
              {part.replace(/<em>/gi, "").replace(/<\/em>/gi, "")}
            </span>
          );
        }
      })}
    </span>
  );
}
